import Collection from '@/store/models/collections/Collection'
import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import { visibleIntroItems } from '@/utils/collection-intro'

/**
 * Turns the raw serialized `introItems` from a collection payload into
 * non-persisted CollectionItem instances, so the intro card and the settings
 * mini rows get the model's display getters (thumbnailUrl, domain, sourceUrl
 * inputs, …) without inserting the copies into the store — inserted they would
 * leak into the grid's per-collection ORM queries.
 *
 * Relationship-named fields (owner, tags, addedBy) clash with the ORM's
 * relation attributes, so like the list normalizers we strip them and set the
 * explicit foreign keys. `$id` must be passed for the `id` getter (and the
 * seeded placeholder art derived from it) to work on a bare instance.
 */
export function hydrateIntroItems(introItems, collectionId) {
  return visibleIntroItems(introItems).map(raw => hydrateDetachedItem(raw, collectionId))
}

/**
 * Same hydration for a single serialized item from any collection-items list
 * payload (also used by the item picker's local, non-ORM item list).
 */
export function hydrateDetachedItem(raw, collectionId) {
  const { owner, tags, addedBy, ...rest } = raw
  return new CollectionItem({
    ...rest,
    $id: raw._id,
    ownerId: collectionId ?? (owner?._id || owner || rest.ownerId || null),
    addedById: addedBy?._id ?? addedBy ?? rest.addedById ?? null,
  })
}

/**
 * Mirrors an edit of an item onto its serialized copy in the collection's `introItems`.
 * Those copies are not the store's CollectionItems, so an edit made on the detail page
 * would otherwise leave the description block showing the old values until a refetch.
 * No-op for an item that is not an intro item.
 */
export function patchIntroItem(collectionId, itemId, changes) {
  return Collection.update({
    where: collectionId,
    data: collection => {
      collection.introItems = collection.introItems.map(raw => (raw._id === itemId ? { ...raw, ...changes } : raw))
    },
  })
}
